/**
 * Chat composer — pill-shaped input with an inline send button.
 *
 * Layout: single row, ~52 px total, send button inline-right inside
 * the input shell. Textarea grows to a few lines via `max-h`, then
 * scrolls. Enter sends; Shift+Enter inserts a newline.
 *
 * Slash autocomplete: parsed on every keystroke; when the parser
 * returns `incomplete`, the floating `SlashPalette` shows above.
 */
import { useState } from "react";
import { SlashPalette } from "./SlashPalette";
import { parseSlash, type ParseResult } from "./slash";

interface Props {
  onSubmit: (parsed: ParseResult) => void;
  isSending: boolean;
}

export function Composer({ onSubmit, isSending }: Props): React.ReactElement {
  const [text, setText] = useState("");
  const parsed = parseSlash(text);
  const showPalette = parsed.type === "incomplete";
  const canSubmit = text.trim().length > 0 && !isSending;

  function submit(): void {
    if (!canSubmit) return;
    onSubmit(parsed);
    if (parsed.type !== "incomplete") {
      setText("");
    }
  }

  return (
    <div
      className="relative shrink-0"
      style={{
        background: "var(--color-bg)",
        borderTop: "1px solid var(--surface-hairline)",
        paddingBottom: "max(8px, env(safe-area-inset-bottom))",
      }}
    >
      {showPalette ? (
        <SlashPalette
          suggestions={parsed.suggestions}
          onPick={(name) => setText(`/${name} `)}
        />
      ) : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="px-3 pt-2"
      >
        <div
          className="flex items-end gap-1.5 rounded-[20px] pl-3 pr-1.5 py-1.5"
          style={{
            background: "var(--color-secondary-bg)",
          }}
        >
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Ask your memory…  /today  /week"
            rows={1}
            className="flex-1 resize-none outline-none bg-transparent text-[14px] leading-[20px] py-1.5 max-h-32 min-h-[20px]"
            style={{
              color: "var(--color-text)",
            }}
            disabled={isSending}
            aria-label="Chat message"
          />
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all disabled:opacity-30 enabled:active:scale-90 enabled:hover:opacity-90"
            style={{
              background: canSubmit ? "var(--color-button)" : "transparent",
              color: canSubmit
                ? "var(--color-button-text)"
                : "var(--color-hint)",
            }}
            aria-label="Send"
          >
            <SendIcon />
          </button>
        </div>
      </form>
    </div>
  );
}

function SendIcon(): React.ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3.4 20.4 20.85 12.92a1 1 0 0 0 0-1.84L3.4 3.6a1 1 0 0 0-1.36 1.21L4 11l8 1-8 1-1.96 6.19a1 1 0 0 0 1.36 1.21z"
        fill="currentColor"
      />
    </svg>
  );
}
